import React, { useState } from 'react';
import { useSet } from 'react-use';
import { useSelector } from 'react-redux';
import { AccordionButton } from '../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { AccordionPanel } from '../../../../CommonComponents/OPL/Accordion/AccordionPanel/AccordionPanel';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { isApexAccountOpen } from '../../../../Apex/ApexHelpers';
import { ApexStatus } from '../../NetWorthSidebarStyles';
import { AccountButton } from './AccountGroup.styles';
import { TruncatedText } from '../../../../CommonComponents/OPL/Text/TruncatedText';
import { AccountGroupName, ICON_MAP, MAX_ITEMS_BEFORE_COLLAPSE } from './AccountGroup.meta';
import { RawButton } from '../../../../CommonComponents/OPL/Button/Button.styles';
import { AccountPerformance } from '../AccountPerformance/AccountPerformance';
import { SelectableAccordionButton } from '../SelectableAccordionButton/SelectableAccordionButton';
import { getIsAccountSelected } from '../../utils/getIsAccountSelected';
import { selectLandingPerformanceReportingTab } from '../../../../CommonSelectors/CommonAreaSelector';
import { unmanagedGroupId } from '../../../../AccountPerformanceReporting/AccountPerformanceReporting.constants';

interface AccountGroupData {
  Name: AccountGroupName;
  Total: number;
  Items: any;
  GroupedBy?: any;
  GroupOrder: { Id: string }[];
  AccountsOverviewGroups?: any;
}

interface ManagedAccountGroup {
  portfolioGroup: {
    id: string;
    name: string;
    nameId: string;
    selectable: string;
  };
  accounts: any[];
}

interface Props {
  clientId: string;
  isInitiallyExpanded?: boolean;
  accountGroup: AccountGroupData;
  selectedAccountId?: string;
  selectedGroupId?: string;
  onAccountClick: (account: any) => void;
  isPerformanceReporting?: boolean;
  hasOrionManagedAccounts?: boolean;
  isManagedAccountsSelected?: boolean;
  isUnmanagedAccountsSelected?: boolean;
  managedAccountGroups: ManagedAccountGroup[];
  unmanagedAccountsLabel: string;
}

export const AccountGroup = (props: Props) => {
  const { isInitiallyExpanded, accountGroup } = props;

  const [isExpanded, setIsExpanded] = useState(!!isInitiallyExpanded);

  const hasAnApexAccountNotOpen = accountGroup.Items.find(
    account => !isApexAccountOpen(account.ApexStatus),
  );

  const buttonId = `networth-widget--group-button--${accountGroup.Name}`;
  const panelId = `networth-widget--panel--${accountGroup.Name}`;

  return (
    <>
      <AccordionButton
        variant="embedded"
        id={buttonId}
        panelId={panelId}
        isActive={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        content={
          <Box display="flex" alignItems="center" width="100%" css="grid-gap: 8px" py="12">
            <Icon className={ICON_MAP[accountGroup.Name]} color="neutral.400" />
            <Text fontWeight={600}>{accountGroup.Name}</Text>
            {hasAnApexAccountNotOpen && <ApexStatus />}
            <Text variant="tabular" fontWeight={600} ml="auto">
              {formatCurrency(accountGroup.Total)}
            </Text>
          </Box>
        }
        css={`
          border-top: 1px solid ${({ theme }) => theme.colors.neutral[200]};
          border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[200]};
        `}
      />

      <AccordionPanel id={panelId} buttonId={buttonId} isActive={isExpanded} variant="embedded">
        <AccountGroupContent {...props} />
      </AccordionPanel>
    </>
  );
};

interface ContentProps extends Props {}

const AccountGroupContent = (props: ContentProps) => {
  const {
    accountGroup,
    hasOrionManagedAccounts,
    isManagedAccountsSelected,
    isUnmanagedAccountsSelected,
    clientId,
    managedAccountGroups,
    selectedGroupId,
    selectedAccountId,
    isPerformanceReporting,
    unmanagedAccountsLabel,
  } = props;

  const landingPerformanceReportingTab = useSelector(selectLandingPerformanceReportingTab);

  if (accountGroup.Name === 'Investment' && hasOrionManagedAccounts) {
    const [managedAccounts, unmanagedAccounts] = accountGroup.Items.reduce(
      ([managedAccountsAcc, unmanagedAccountsAcc], account) =>
        account.Item && account.Item.IsOrionManagedAccount
          ? [[...managedAccountsAcc, account], unmanagedAccountsAcc]
          : [managedAccountsAcc, [...unmanagedAccountsAcc, account]],
      [[], []],
    );

    const groupExpandedSet = new Set<string>();

    const groupWithSelectedAccount = Object.values(managedAccountGroups).find(({ accounts }) =>
      accounts.some(({ Item }) => Item.AccountId === selectedAccountId),
    );

    // Just one group -> expand it
    if (Object.keys(managedAccountGroups).length === 1) {
      groupExpandedSet.add(Object.values(managedAccountGroups)[0].portfolioGroup.id);
    }

    // Expand the group with the selected account
    if (groupWithSelectedAccount) {
      groupExpandedSet.add(groupWithSelectedAccount.portfolioGroup.id);
    }

    let isUnmanagedInitiallyExpanded = false;

    // Total number of groups + total number of accounts is less than MAX_ITEMS_BEFORE_COLLAPSE
    // -> expand all groups and the unmanaged section
    if (
      Object.keys(managedAccountGroups).length + managedAccounts.length + unmanagedAccounts.length <
      MAX_ITEMS_BEFORE_COLLAPSE
    ) {
      Object.values(managedAccountGroups).forEach(({ portfolioGroup }) =>
        groupExpandedSet.add(portfolioGroup.id),
      );

      isUnmanagedInitiallyExpanded = true;
    }

    const [isManagedExpanded, setIsManagedExpanded] = useState(
      () =>
        isPerformanceReporting ||
        managedAccounts.some(account => getIsAccountSelected(account, selectedAccountId)),
    );

    const [isUnmanagedExpanded, setIsUnmanagedExpanded] = useState(
      () =>
        isUnmanagedInitiallyExpanded ||
        unmanagedAccounts.some(account => getIsAccountSelected(account, selectedAccountId)),
    );

    const [, { has: isGroupExpanded, toggle: toggleGroupExpanded }] = useSet(groupExpandedSet);

    const { GroupedBy: accountsGroupedBy, GroupOrder } = accountGroup;

    const unmanagedAccountsTotal = unmanagedAccounts.reduce((acc, cur) => acc + cur.Value, 0);

    // Some managed accounts can be in more than one group -> we can't just sum the totals
    // by doing managedAccounts.reduce((acc, cur) => acc + cur.Value, 0) because we would
    // count the same account multiple times.
    // Solution: calculate the total by subtracting the unmanaged accounts total from the
    // investment group total
    const managedAccountsTotal = accountGroup.Total - unmanagedAccountsTotal;

    return (
      <>
        {managedAccounts.length > 0 && (
          <>
            <SelectableAccordionButton
              id="networth-widget--managed--group-button"
              panelId="networth-widget--managed--panel"
              isExpanded={isManagedExpanded}
              isSelected={!!isManagedAccountsSelected}
              onClick={() => {
                if (!isManagedAccountsSelected) {
                  ADV.util.Router.go2('AccountPerformanceReporting', {
                    clientId,
                    tabSlug: landingPerformanceReportingTab,
                    isApex: false,
                  });
                }
              }}
              onToggleExpand={() => setIsManagedExpanded(!isManagedExpanded)}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flex={1}>
                <Text fontWeight={600} fontSize="small" color="inherit">
                  Managed
                </Text>
                <Text variant="tabular" fontWeight={600} fontSize="small" color="inherit">
                  {formatCurrency(managedAccountsTotal)}
                </Text>
              </Box>
            </SelectableAccordionButton>

            <AccordionPanel
              id="networth-widget--managed--panel"
              buttonId="networth-widget--managed--group-button"
              isActive={isManagedExpanded}
              variant="embedded"
            >
              {accountsGroupedBy !== 'dummyGrouping' &&
              Object.keys(managedAccountGroups).length > 0 ? (
                GroupOrder.reduce((acc, { Id }) => {
                  if (!Id) {
                    console.error(`Group with id: ${Id} found in GroupOrder`);
                    return acc;
                  }

                  const foundGroup = managedAccountGroups[Id];

                  if (!foundGroup) {
                    console.error(`Group with id: ${Id} not found in managedAccountGroups`);
                    return acc;
                  }

                  acc.push(foundGroup);

                  return acc;
                }, [] as ManagedAccountGroup[]).map(({ portfolioGroup, accounts }) => (
                  <React.Fragment key={portfolioGroup.id}>
                    <SelectableAccordionButton
                      id={`networth-widget--${portfolioGroup.id}--group-button`}
                      panelId={`networth-widget--${portfolioGroup.id}--panel`}
                      isExpanded={isGroupExpanded(portfolioGroup.id)}
                      isSelected={selectedGroupId === portfolioGroup.id}
                      onClick={
                        portfolioGroup.selectable
                          ? () => {
                              ADV.util.Router.go2('AccountPerformanceReporting', {
                                clientId,
                                groupId: portfolioGroup.id,
                                tabSlug: landingPerformanceReportingTab,
                                isApex: false,
                              });
                            }
                          : undefined
                      }
                      onToggleExpand={() => toggleGroupExpanded(portfolioGroup.id)}
                      variant="secondary"
                    >
                      <TruncatedText color="primary.600" textAlign="left" fontWeight={500}>
                        {portfolioGroup.name}
                      </TruncatedText>

                      <Box display="flex">
                        {portfolioGroup.nameId && (
                          <Text fontWeight={600} fontSize="small" color="primary.600">
                            {portfolioGroup.nameId}
                          </Text>
                        )}

                        <Text
                          variant="tabular"
                          color="primary.600"
                          fontWeight={600}
                          fontSize="small"
                          ml="auto"
                        >
                          {formatCurrency(
                            accounts.reduce((sum, account) => sum + account.Value, 0),
                          )}
                        </Text>
                      </Box>
                    </SelectableAccordionButton>

                    {isGroupExpanded(portfolioGroup.id) && (
                      <Box
                        id={`networth-widget--${portfolioGroup.id}--panel`}
                        role="region"
                        aria-hidden={!isGroupExpanded(portfolioGroup.id)}
                      >
                        <AccountsList
                          {...props}
                          accounts={accounts}
                          accountsGroupedBy={accountsGroupedBy}
                          landingPerformanceReportingTab={landingPerformanceReportingTab}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <AccountsList
                  {...props}
                  accounts={managedAccounts}
                  landingPerformanceReportingTab={landingPerformanceReportingTab}
                />
              )}
            </AccordionPanel>
          </>
        )}

        {unmanagedAccounts.length > 0 && (
          <>
            <SelectableAccordionButton
              id="networth-widget--unmanaged--group-button"
              panelId="networth-widget--unmanaged--panel"
              isExpanded={isUnmanagedExpanded}
              isSelected={!!isUnmanagedAccountsSelected}
              onToggleExpand={() => setIsUnmanagedExpanded(!isUnmanagedExpanded)}
              onClick={
                unmanagedAccounts.every(
                  account => account.Item && account.Item.IsOrionReportableAccount,
                ) &&
                (() => {
                  ADV.util.Router.go2('AccountPerformanceReporting', {
                    clientId,
                    groupId: unmanagedGroupId,
                    tabSlug: landingPerformanceReportingTab,
                    isApex: false,
                  });
                })
              }
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flex={1}>
                <Text fontWeight={600} fontSize="small" color="inherit">
                  {unmanagedAccountsLabel}
                </Text>
                <Text variant="tabular" fontWeight={600} fontSize="small" color="inherit">
                  {formatCurrency(unmanagedAccountsTotal)}
                </Text>
              </Box>
            </SelectableAccordionButton>

            <AccordionPanel
              variant="embedded"
              id="networth-widget--unmanaged--panel"
              buttonId="networth-widget--unmanaged--group-button"
              isActive={isUnmanagedExpanded}
            >
              <AccountsList {...props} accounts={unmanagedAccounts} />
            </AccordionPanel>
          </>
        )}
      </>
    );
  }

  return <AccountsList {...props} accounts={accountGroup.Items} />;
};

interface AccountsListProps extends Props {
  accounts: any[];
  accountsGroupedBy?: string;
  landingPerformanceReportingTab?: string;
}

const AccountsList = ({
  accounts,
  selectedAccountId,
  onAccountClick,
  isPerformanceReporting,
  landingPerformanceReportingTab,
  accountsGroupedBy,
  clientId,
}: AccountsListProps) => {
  return (
    <>
      {accounts.map(account => {
        const ApexId = isApexAccountOpen(account.ApexStatus)
          ? account.Item.AccountId
          : account.ManagedAccountId;

        const isSelected =
          selectedAccountId === (ApexId || account.Item.AccountId || account.Item.Id);

        const isApexPendingAction = !isApexAccountOpen(account.ApexStatus);

        const showAccountPerformance =
          account.orionName && isPerformanceReporting && account.performance != null;

        const accountName = account?.Item?.Description || account.Name;

        return (
          <AccountButton
            key={ApexId || account.Item.AccountId || account.Item.Id}
            isSelected={isSelected}
            disabled={!account.IsEditable}
            onClick={() => {
              if (!isSelected && account.IsEditable) {
                onAccountClick({
                  ...account,
                  isApexPendingAction,
                  isApex: (account.IsApexOrion && isApexAccountOpen(account.ApexStatus)) || false,
                  landingPerformanceReportingTab,
                });
              }
            }}
          >
            <Box display="flex" alignItems="center">
              {account.IsLinked && account.IsLinkBroken && (
                <RawButton
                  onClick={ev => {
                    ev.stopPropagation();

                    ADV.util.Router.go2('ProfileAccounts', {
                      clientId,
                      tabSlug: 'manageLinks',
                    });
                  }}
                  css="display: grid; place-content: center; width: 20px; height: 16px; margin-right: 8px;"
                >
                  <Icon className="fas fa-exclamation-triangle" color="warning.300" />
                </RawButton>
              )}

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                css="gap: 8px"
              >
                <TruncatedText width="100%" textAlign="left" fontWeight={500} css="flex: 1">
                  {accountName}
                </TruncatedText>

                {showAccountPerformance && <AccountPerformance value={account.performance} />}
              </Box>
            </Box>

            <Box display="flex">
              {account.orionName &&
                isPerformanceReporting &&
                accountsGroupedBy !== 'registrationId' && (
                  <TruncatedText fontWeight={500} fontSize="small" color="neutral.600" mr="8">
                    {account.orionName}
                  </TruncatedText>
                )}

              {isApexPendingAction ? (
                <Box display="flex" alignItems="center" ml="auto" css="gap: 4px">
                  <ApexStatus />
                  <Text fontWeight={500} fontSize="small" color="neutral.500">
                    {account.ApexStatus}
                  </Text>
                </Box>
              ) : (
                <Text variant="tabular" fontWeight={600} fontSize="small" ml="auto">
                  {formatCurrency(account.Value)}
                </Text>
              )}
            </Box>
          </AccountButton>
        );
      })}
    </>
  );
};
